/**
 * This is the base class from which all client-side views should inherit.
 *
 * This view provides base functionality for a variety of operations in an
 * effort to minimize unique snowflakes.
 *
 */

var Backbone = require('js/lib/sw-backbone');
var _ = require('underscore');
var $ = require('js/sw-jquery/sw-jquery');
var sw = require('js/lib/sw');
var swUtils = require('js/lib/sw-utils');
var ShapewaysModel = require('js/lib/models/ShapewaysModel');

var utils = require('js/lib/sw-utils');


/**
 * This mutation channel will act as a *very* very local
 * version of the Flux dispatcher idea with a *very* very
 * tightly scoped purpose and a single input source.
 *
 * It will be used for dispatching information about current
 * DOM mutations to the views of the Shapeways Applications.
 * With this information they can react according to their
 * specification.
 *
 * An immediate use case for this will be to help inform
 * views that their associated DOM element has been removed
 * from the DOM. With that information, they can thus react
 * with sophisticated teardown logic so they may be garbage
 * collected and discarded. This will also help contain
 * leaky references.
 *
 * If we feel that the scope of this functionality increases
 * we can separate this channel into its own module.
 */
var mutationChannel = _.extend({}, Backbone.Events);
var mutationEvent = '_mutationEvent';

/**
 * Instantiate a single mutation observer to keep the
 * memory footprint of the application low and to
 * make debugging and centralizing logic easier.
 *
 * This observer will observe mutations on the body
 * of the document and dispatch an event when there
 * are mutations. It is up to the browser engine to
 * optimally batch these events. The spec for
 * MutationObserver stipulates that this will happen
 * asynchronously so that the browser doesn't end up
 * blocking the application in a way that penalizes
 * performance.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
 */
var LocalMutationObserver = window.MutationObserver || null;
var swMutationObserver;
if (LocalMutationObserver) {
  swMutationObserver = new LocalMutationObserver(function(mutationList) {
    mutationChannel.trigger(mutationEvent, mutationList);
  });

  /**
   * This can be updated later as the needs arise.
   */
  swMutationObserver.observe((document.documentElement || document.body), {
    childList: true,
    subtree: true
  });
}




// Shapeways specific instance props to be added to each extended view as a cloned property
// so as there is no information leaking between views
var SHAPEWAYS_INSTANCE_PROPS = {
  swSelectors:  {},
  swAttrs:      {},
  viewDefaults: {}
};

// var SHAPEWAYS_ATTR_PREFIX = 'data-sw-';
var SHAPEWAYS_ATTR_PREFIX = '';

/**
 * Polyfill request animation frame. Just in case.
 */
require('js/lib/request-animation-pollyfill');

function _makeSWAttr(name) {
  return _.isString(name) ? SHAPEWAYS_ATTR_PREFIX + name : name;
}

function _makeAttrSelector(name) {
  var prefixedName = _.isString(name) && name.indexOf(SHAPEWAYS_ATTR_PREFIX) === 0 ? name : _makeSWAttr(name);
  return _.isString(prefixedName) ? '[' + prefixedName + ']' : prefixedName;
}

var ShapewaysView = Backbone.View.extend({

  token: '',

  // Must be set in constructor otherwise ALL views will share the object
  cachedElements: undefined,
  state: null,

  /**
   * We have been developing the application under the assumption that views
   * go away when they are removed. This switch allows us to opt out of that
   * assumption, but the default is to maintain the assumption.
   *
   * There should not be many instances where this assumption is invalid
   *
   * @type {Boolean}
   */
  autoRemove: true,

  _ensureElement: function _ensureElement() {

    Backbone.View.prototype._ensureElement.apply(this, arguments);

    this.cacheSwSelectors();
    this._createAutoRemoveListener();
  },

  /**
   * Create a listener for the mutation channel for this view
   * if this view should automatically execute teardown logic
   * upon removal from the DOM.
   *
   * The solution in this code will determine if the el
   * is a child of <html>, hardRemoving the view if the el
   * is no longer a child of <html>.
   *
   * This is generally performant as generally one only removes
   * a single node from the top to remove a chunk of html.
   *
   * parents('body').length === 0 checks if the element has been
   * removed from the DOM. 'parents' is also quite fast as it performs a linear
   * traversal on the order of the height of the DOM tree.
   *
   * This will be a performance bottleneck in applications
   * that have a lot of DOM removals containing a lot of
   * ShapewaysViews.
   *
   */
  _createAutoRemoveListener: function _createAutoRemoveListener() {
    if (window.MutationObserver && this.autoRemove) {
      this.listenTo(mutationChannel, mutationEvent, function(mutationRecordList) {

        var removed = _.some(mutationRecordList, function(mutationRecord) {
          return mutationRecord.removedNodes.length > 0;
        }, this);

        if (removed && this.$el.parents('html').length === 0) {
          // Kill it with fire.
          this.hardRemove();
        }
      });
    }
  },

  /**
   * Creates a cached element for each swSelector.
   */
  cacheSwSelectors: function cacheSwSelectors() {
    _.each(this.swSelectors, function(selector, selectorName) {
      this.cacheElements(selector, '$' + selectorName);
    },this);
  },

  constructor: function(options) {
    // Safeguard against empty options. It happens!
    options = options || {};

    // Set up local state

    if (options.stateId) {
      var _this = this;
      var _arguments = arguments;

      require(['js/lib/models/ModelManager'], function(ModelManager) {

        _this.state = ModelManager.getViewState(options.stateId);
        _this.state.set(_this.viewDefaults);

        // Keep this from leaking
        _this.cachedElements = {};

        // Normal constructor
        Backbone.View.apply(_this, _arguments);

      });

    } else {

      this.state = new ShapewaysModel(this.viewDefaults);

      // Keep this from leaking
      this.cachedElements = {};

      Backbone.View.apply(this, arguments);
    }
  },

  repaint: function(continuation) {

    if (!continuation || (typeof continuation !== 'function')) {
      continuation = _.bind(this.render, this);
    }

    window.requestAnimationFrame(continuation);
  },

  hide: function() {
    this.$el.hide();
  },

  show: function() {
    this.$el.show();
  },

  clearCachedElements: function(name) {
    if(name){
      delete this.cachedElements[name];
    } else {
      this.cachedElements = {};
    }
  },

  cacheElements: function(string, name) {
    this.cachedElements[name] = this.$el.swFindWithSelf(string);
  },

  /**
   *
   * Takes an html fragment and replaces the current view as well as triggers
   * sw-fragment-loaded on the new fragment.
   *
   * @param {string} fragment
   * @param {function} callback
   */
  replaceWith: function(fragment, callback) {
    var $fragment;

    if (fragment instanceof $) {
      $fragment = $(fragment.swOuterHtml());
    } else {
      $fragment = $(fragment);
    }

    this.repaint(_.bind(function() {
      this.$el.hide().after($fragment).remove();

      sw.trigger('sw-fragment-loaded', $fragment);

      if (callback && (typeof callback === 'function')) {
        callback();
      }
    }, this));
  },

  makeSWAttr: function makeSWAttr(name) {
    return _.isObject(name) ? _.mapObject(name, _makeSWAttr) : _makeSWAttr(name);
  },

  makeSWAttrSelector: function makeSWAttrSelector(name) {
    return _.isObject(name) ? _.mapObject(name, _makeAttrSelector) : _makeAttrSelector(name);
  },

  /**
   * Makes a attribute selector
   * @param  {string} attr            attribute name
   * @param  {string} value           value for selector
   * @param  {obj}    options         options for selector
   *    selectorType  default '='     operation used to asses value
   * @return {string}                 full attribute selector
   */
  makeAttributeValueSelector: swUtils.makeAttributeValueSelector,

  attrBool: function attrBool(attrResult) {
    return _.isUndefined(attrResult) || (attrResult === false);
  },

  /**
   * Formats US currency for display.
   *
   * @param  {string|number} currency String or number to be formatted.
   * @return {string} Formatted US currency fixed to two decimal places.
   */
  formatUSCurrency: function formatCurrency(currency) {
    var formattedCurrency = parseFloat(currency).toFixed(2);
    return '$' + formattedCurrency;
  },

  /**
   * Clean up all external references for the view and let the garbage
   * collector remove it at its next pass.
   *
   * This will not remove a dom element.
   */
  softRemove: function softRemove() {
    this.undelegateEvents();
    this.stopListening();
    this.state.stopListening();
    this.setElement(null);
    this.model = null;
  },

  /**
   * Destroys the view and removes the dom element.
   */
  hardRemove: function hardRemove() {
    this.remove();
    this.softRemove();
  }
});

// Attach our static props to the base view
ShapewaysView.prototype = _.extend(ShapewaysView.prototype, SHAPEWAYS_INSTANCE_PROPS);

function initializeViewAttrs(newView) {

  // Clone a fresh static prop for each child view.
  _.each(SHAPEWAYS_INSTANCE_PROPS, function(propertyDefault, propertyName) {
    if (!newView.hasOwnProperty(propertyName)) {
      newView[propertyName] = utils.cloneDataObject(propertyDefault);
    }
  });

  return _.extend(newView, {
    swSelectors: _.extend(newView.swSelectors, ShapewaysView.prototype.makeSWAttrSelector(newView.swAttrs))
  });
}

ShapewaysView.extend = function shapewaysViewExtend() {
  var NewView = Backbone.View.extend.apply(this, arguments);
  NewView.prototype = initializeViewAttrs(NewView.prototype);
  return NewView;
};

module.exports = ShapewaysView;
